import { SHIPMENT_KIND_NAMES, type ShipmentKind, type ShipmentRow } from '@pms/shared';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuthStore } from '../../auth/store.js';
import { db } from '../../sync/db.js';
import { request } from '../../sync/api.js';
import { syncNow } from '../../sync/engine.js';
import { enqueue } from '../../sync/outbox.js';
import { useLiveQuery } from 'dexie-react-hooks';

/**
 * Отгрузка продукции и возвраты.
 *
 * Именно этот документ списывает продукцию со склада. Палеты, отмеченные
 * бригадой в сменном отчёте, — мера труда: если бы списывали и они, одна и та
 * же продукция ушла бы дважды.
 *
 * Возврат — тот же документ с обратным знаком, поэтому и форма одна.
 */
export function ShipmentsScreen(): JSX.Element {
  const user = useAuthStore((state) => state.user);
  const sizes = useLiveQuery(() => db.productSizes.orderBy('sortOrder').toArray(), [], []);
  const [shipments, setShipments] = useState<ShipmentRow[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [kind, setKind] = useState<ShipmentKind>('SHIPMENT');
  const [customer, setCustomer] = useState('');
  const [document, setDocument] = useState('');
  const [note, setNote] = useState('');
  const [rows, setRows] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    try {
      const list = await request<{ shipments: ShipmentRow[] }>(
        '/api/shipments',
        { method: 'GET' },
        20_000,
      );
      setShipments(list.shipments);
      setError(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось загрузить документы');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const submit = async (): Promise<void> => {
    if (!user) return;

    const lines = Object.entries(rows)
      .filter(([, value]) => value.trim() !== '' && Number(value) > 0)
      .map(([productSizeId, value]) => ({ productSizeId, qtyPieces: Number(value) }));

    if (lines.length === 0) {
      setError('Укажите хотя бы один типоразмер');
      return;
    }
    if (customer.trim().length < 2 || document.trim().length === 0) {
      setError('Укажите покупателя и номер документа');
      return;
    }

    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const shipmentId = crypto.randomUUID();
      await enqueue({
        type: 'SHIPMENT_RECORDED',
        aggregateType: 'SHIPMENT',
        aggregateId: shipmentId,
        userId: user.id,
        payload: {
          shipmentId,
          kind,
          at: new Date().toISOString(),
          customer: customer.trim(),
          documentNumber: document.trim(),
          note: note.trim() || null,
          lines,
        },
      });
      await syncNow();
      await load();
      setAdding(false);
      setCustomer('');
      setDocument('');
      setNote('');
      setRows({});
      setNotice(kind === 'SHIPMENT' ? 'Отгрузка записана' : 'Возврат записан');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не получилось записать');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen px-4 py-6">
      <header className="mb-6 flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">Отгрузка</h1>
        <Link to="/" className="rounded-xl bg-muted px-4 py-3 text-base font-semibold">
          Назад
        </Link>
      </header>

      {error && <p className="mb-4 rounded-xl bg-rose-50 text-rose-800 ring-1 ring-rose-200 px-4 py-3 text-base">{error}</p>}
      {notice && (
        <p className="mb-4 rounded-xl bg-muted px-4 py-3 text-base text-body">{notice}</p>
      )}

      {!adding && (
        <button
          type="button"
          onClick={() => {
            setAdding(true);
            setNotice(null);
          }}
          className="mb-6 w-full rounded-xl bg-emerald-600 px-5 py-4 text-lg font-semibold text-white active:bg-emerald-700"
        >
          Оформить документ
        </button>
      )}

      {adding && (
        <section className="mb-6 rounded-xl bg-card shadow-card p-4">
          <div className="mb-4 flex gap-2">
            <button
              type="button"
              onClick={() => setKind('SHIPMENT')}
              className={`flex-1 rounded-xl px-4 py-3 text-base font-semibold ${
                kind === 'SHIPMENT' ? 'bg-sky-700 text-white' : 'bg-muted'
              }`}
            >
              Отгрузка
            </button>
            <button
              type="button"
              onClick={() => setKind('RETURN')}
              className={`flex-1 rounded-xl px-4 py-3 text-base font-semibold ${
                kind === 'RETURN' ? 'bg-amber-700 text-white' : 'bg-muted'
              }`}
            >
              Возврат
            </button>
          </div>

          <div className="flex flex-col gap-3">
            <input
              value={customer}
              onChange={(event) => setCustomer(event.target.value)}
              placeholder="Покупатель"
              className="rounded-xl bg-muted px-4 py-3 text-base"
            />
            <input
              value={document}
              onChange={(event) => setDocument(event.target.value)}
              placeholder="Номер документа"
              className="rounded-xl bg-muted px-4 py-3 text-base"
            />
            <input
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="Примечание (необязательно)"
              className="rounded-xl bg-muted px-4 py-3 text-base"
            />

            <p className="mt-2 text-base text-dim">Количество, штук:</p>
            {sizes.map((size) => (
              <div key={size.id} className="flex items-center gap-3">
                <span className="flex-1 text-base">{size.name}</span>
                <input
                  value={rows[size.id] ?? ''}
                  onChange={(event) =>
                    setRows({ ...rows, [size.id]: event.target.value.replace(/\D/g, '') })
                  }
                  inputMode="numeric"
                  placeholder="шт"
                  className="w-28 rounded-xl bg-muted px-3 py-3 text-base tabular-nums"
                />
              </div>
            ))}
          </div>

          <div className="mt-5 flex gap-3">
            <button
              type="button"
              disabled={busy}
              onClick={() => void submit()}
              className="flex-1 rounded-xl bg-sky-700 px-5 py-4 text-lg font-semibold disabled:opacity-50"
            >
              {busy ? 'Записываю…' : 'Записать'}
            </button>
            <button
              type="button"
              onClick={() => setAdding(false)}
              className="rounded-xl bg-muted px-5 py-4 text-lg font-semibold"
            >
              Отмена
            </button>
          </div>
        </section>
      )}

      <h2 className="mb-3 text-lg font-semibold">Последние документы</h2>
      <div className="flex flex-col gap-3">
        {shipments !== null && shipments.length === 0 && (
          <p className="rounded-xl bg-card shadow-card p-4 text-base text-dim">Пока пусто.</p>
        )}
        {(shipments ?? []).map((shipment) => (
          <article
            key={shipment.id}
            className={`rounded-xl p-4 ${shipment.kind === 'RETURN' ? 'bg-canvas' : 'bg-card shadow-card'}`}
          >
            <div className="flex items-baseline justify-between gap-3">
              <p className="text-lg font-semibold">{shipment.customer}</p>
              <p className="text-base text-dim">{SHIPMENT_KIND_NAMES[shipment.kind]}</p>
            </div>
            <p className="text-base text-dim">
              документ {shipment.documentNumber} ·{' '}
              {new Date(shipment.at).toLocaleDateString('ru-RU')} · {shipment.byUserName}
            </p>
            <ul className="mt-2 flex flex-col gap-1">
              {shipment.lines.map((line) => (
                <li key={line.productSizeId} className="text-base text-body">
                  {line.name}: {line.qtyPieces} шт
                </li>
              ))}
            </ul>
            {shipment.note && <p className="mt-1 text-base text-dim">{shipment.note}</p>}
          </article>
        ))}
      </div>
    </div>
  );
}
